import { Check } from 'lucide-react'

import { cn } from '@/utils'
import { PlaceholderSheet } from './PlaceholderSheet'
import { SHEET_ASPECT_RATIO } from './strip'
import type { DocumentPage } from './types'

export const PAGE_WIDTH = 108
const ART_INSET = 8

// How the sheet is drawn: the page itself, a plain sheet while the strip is moving, or a pulsing
// sheet while the image is still coming.
export type PageSheet = 'art' | 'plain' | 'loading'

interface PageCardProps {
  page: DocumentPage
  selected: boolean
  active: boolean
  sheet: PageSheet
  src?: string
  onActivate: (page: number, additive: boolean) => void
}

// The sheet number is the page's name; its PDF is named once, on the band. Blue means picked up
// and nothing else here is blue. The dark outline marks the page in the reader.
export const PageCard = ({
  page,
  selected,
  active,
  sheet,
  src,
  onActivate,
}: PageCardProps) => {
  const sheetWidth = PAGE_WIDTH - ART_INSET
  const pickedUp = cn(selected && 'border-blue-600 ring-2 ring-blue-600')

  return (
    <button
      type="button"
      data-page-number={page.number}
      onClick={(event) => {
        if (event.detail === 0) {
          onActivate(page.number, event.ctrlKey || event.metaKey)
        }
      }}
      style={{ width: PAGE_WIDTH }}
      title={`${page.source.fileName}, page ${page.source.pageInFile}`}
      className={cn(
        'relative cursor-pointer rounded-lg p-1 text-left transition-colors',
        selected
          ? 'cursor-grab bg-blue-100 active:cursor-grabbing'
          : 'hover:bg-neutral-200/70',
        active && 'outline-2 -outline-offset-2 outline-neutral-800',
      )}
    >
      {sheet === 'art' && src ? (
        <img
          src={src}
          draggable={false}
          alt={`Page ${page.source.pageInFile} of ${page.source.fileName}`}
          style={{ width: sheetWidth, aspectRatio: SHEET_ASPECT_RATIO }}
          className={cn(
            'shrink-0 rounded-sm border border-neutral-300 bg-white object-contain shadow-sm',
            pickedUp,
          )}
        />
      ) : (
        <PlaceholderSheet
          width={sheetWidth}
          loading={sheet !== 'plain'}
          className={pickedUp}
        />
      )}

      {selected ? (
        <span className="absolute right-2 top-2 flex size-4 items-center justify-center rounded-full bg-blue-600 shadow-sm">
          <Check className="size-3 text-white" strokeWidth={3} />
        </span>
      ) : null}

      <span
        className={cn(
          'mt-1 block h-4 truncate text-[11px] leading-4',
          selected ? 'font-medium text-blue-700' : 'text-ink-subtle',
        )}
      >
        Page {page.source.pageInFile}
      </span>
    </button>
  )
}
